import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authAPI, usersAPI } from '../../services/api';
import { FiLock, FiEye, FiEyeOff, FiBell, FiUser, FiTrash2, FiShield } from 'react-icons/fi';
import toast from 'react-hot-toast';

const Settings = () => {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('password');

  // Password change
  const [passwords, setPasswords] = useState({ current: '', new_password: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new_password: false, confirm: false });
  const [savingPassword, setSavingPassword] = useState(false);

  // Notification preferences
  const [notifications, setNotifications] = useState({
    email_job_alerts: true,
    email_application_updates: true,
    email_messages: true,
    email_newsletter: false,
  });
  const [savingNotifications, setSavingNotifications] = useState(false);

  // Delete account
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!passwords.current) { toast.error('Enter your current password'); return; }
    if (!passwords.new_password) { toast.error('Enter a new password'); return; }
    if (passwords.new_password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (passwords.new_password !== passwords.confirm) { toast.error('Passwords do not match'); return; }

    setSavingPassword(true);
    try {
      await authAPI.resetPassword({
        current_password: passwords.current,
        password: passwords.new_password,
        password_confirmation: passwords.confirm,
      });
      toast.success('Password updated successfully');
      setPasswords({ current: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Failed to update password');
    }
    setSavingPassword(false);
  };

  const handleNotificationsSave = async (e) => {
    e.preventDefault();
    setSavingNotifications(true);
    try {
      await usersAPI.update(user.id, { notification_preferences: notifications });
      toast.success('Notification preferences saved');
    } catch (err) {
      toast.error('Failed to save preferences');
    }
    setSavingNotifications(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') { toast.error('Type DELETE to confirm'); return; }
    setDeleting(true);
    try {
      await usersAPI.delete(user.id);
      toast.success('Account deleted');
      await logout();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete account');
    }
    setDeleting(false);
  };

  const togglePassword = (field) => setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });

  return (
    <div className="static-page">
      <div className="container" style={{ maxWidth: '700px' }}>
        <div className="page-header">
          <h1>Account Settings</h1>
          <p>Manage your password, notifications, and account preferences</p>
        </div>

        <div className="tabs" style={{ marginBottom: '1.5rem' }}>
          <button className={`tab ${tab === 'password' ? 'active' : ''}`} onClick={() => setTab('password')}><FiLock style={{ marginRight: '0.35rem' }} /> Password</button>
          <button className={`tab ${tab === 'notifications' ? 'active' : ''}`} onClick={() => setTab('notifications')}><FiBell style={{ marginRight: '0.35rem' }} /> Notifications</button>
          <button className={`tab ${tab === 'account' ? 'active' : ''}`} onClick={() => setTab('account')}><FiShield style={{ marginRight: '0.35rem' }} /> Account</button>
        </div>

        {/* PASSWORD TAB */}
        {tab === 'password' && (
          <div className="card" style={{ padding: '2rem' }}>
            <h3 style={{ marginBottom: '0.5rem' }}>Change Password</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
              Use a strong password with at least 8 characters.
            </p>
            <form onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label>Current Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    placeholder="Enter current password"
                    value={passwords.current}
                    onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                  />
                  <button type="button" onClick={() => togglePassword('current')} style={eyeBtn}>
                    {showPasswords.current ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>New Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswords.new_password ? 'text' : 'password'}
                    placeholder="Minimum 8 characters"
                    value={passwords.new_password}
                    onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                  />
                  <button type="button" onClick={() => togglePassword('new_password')} style={eyeBtn}>
                    {showPasswords.new_password ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    placeholder="Repeat new password"
                    value={passwords.confirm}
                    onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                  />
                  <button type="button" onClick={() => togglePassword('confirm')} style={eyeBtn}>
                    {showPasswords.confirm ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={savingPassword}>
                {savingPassword ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        )}

        {/* NOTIFICATIONS TAB */}
        {tab === 'notifications' && (
          <div className="card" style={{ padding: '2rem' }}>
            <h3 style={{ marginBottom: '0.5rem' }}>Email Notifications</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
              Choose which emails you'd like to receive.
            </p>
            <form onSubmit={handleNotificationsSave}>
              {[
                { key: 'email_job_alerts', label: 'Job Alerts', desc: 'New jobs matching your skills and preferences' },
                { key: 'email_application_updates', label: 'Application Updates', desc: 'Status changes on your applications' },
                { key: 'email_messages', label: 'Messages', desc: 'New messages from recruiters or candidates' },
                { key: 'email_newsletter', label: 'Newsletter', desc: 'Tips, platform updates, and career insights' },
              ].map(({ key, label, desc }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid var(--border, #eee)' }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{label}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>{desc}</div>
                  </div>
                  <div
                    className={`toggle ${notifications[key] ? 'active' : ''}`}
                    onClick={() => setNotifications({ ...notifications, [key]: !notifications[key] })}
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                  />
                </div>
              ))}
              <button type="submit" className="btn btn-primary" style={{ marginTop: '1.25rem' }} disabled={savingNotifications}>
                {savingNotifications ? 'Saving...' : 'Save Preferences'}
              </button>
            </form>
          </div>
        )}

        {/* ACCOUNT TAB */}
        {tab === 'account' && (
          <>
            <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
              <h3 style={{ marginBottom: '0.75rem' }}>Account Information</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.9rem' }}>
                <div><strong>Name:</strong> {user?.first_name} {user?.last_name}</div>
                <div><strong>Email:</strong> {user?.email}</div>
                <div><strong>Role:</strong> {(user?.roles || []).map(r => typeof r === 'string' ? r : r.name).join(', ') || 'Member'}</div>
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.75rem' }}>
                To change your name, phone, or bio, visit your <a href="/profile" style={{ color: 'var(--accent-primary, #3b82f6)' }}>Profile</a>.
              </p>
            </div>

            <div className="card" style={{ padding: '2rem', borderLeft: '3px solid var(--accent-danger, #ef4444)' }}>
              <h3 style={{ color: 'var(--accent-danger, #ef4444)', marginBottom: '0.5rem' }}>
                <FiTrash2 style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
                Delete Account
              </h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                This will permanently remove your account, profile, applications, and all associated data. This action cannot be undone.
              </p>
              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '0.85rem' }}>Type <strong>DELETE</strong> to confirm</label>
                <input
                  type="text" placeholder="DELETE"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  style={{ maxWidth: '250px' }}
                />
              </div>
              <button
                className="btn btn-danger"
                disabled={deleteConfirm !== 'DELETE' || deleting}
                onClick={handleDeleteAccount}
                style={{ background: 'var(--accent-danger, #ef4444)', color: '#fff', border: 'none' }}
              >
                {deleting ? 'Deleting...' : 'Delete My Account'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const eyeBtn = {
  position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
  display: 'flex', alignItems: 'center', padding: '0.25rem',
};

export default Settings;
